import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../environments/environment";

export interface Contrato {
  objeto: null;
  disponibilidad: any;
  empresaNit: Contrato | undefined;
  id?: number;
  numero: number;
  cedula: number;
  contratante: string;
  direccion?: string;
  ciudad?: string;
  telefono?: string;
  mail?: string;
  inicio?: string;
  fin?: string;
  origen?: string;
  destino?: string;
  tipo?: string;
  idres?: number;
  responsable?: string;
  dirresponsble?: string;
  telres?: string;
  cuires?: string;
  mailresp: string;
  pasajeros?: number;
  valor?: number;
  dispobilidad?: number;
}

@Injectable({
  providedIn: "root",
})
export class ContratosService {
  private apiUrl =  `${environment.apiUrl}/api/contratos`; 
  

  constructor(private http: HttpClient) {}

  createContrato(contrato: any): Observable<any> {
    return this.http.post<any>(this.apiUrl, contrato, {
      withCredentials: true,
    }).pipe(
      catchError((error) => {
        const message = error.status === 400 && error.error?.message
          ? error.error.message
          : "Error al crear el contrato.";
        return throwError(() => new Error(message));
      })
    );
  }

  getContratoPorNumero(numero: number): Observable<Contrato> {
    return this.http.get<Contrato>(`${this.apiUrl}/numero/${numero}`, {
      withCredentials: true
    });
  }

  updateContratoPorNumero(numero: number, contrato: Contrato): Observable<Contrato> {
    return this.http.put<Contrato>(`${this.apiUrl}/editar/${numero}`, contrato, {
      withCredentials: true
    }).pipe(
      catchError((error) => {
        const message = error.status === 404
          ? "Contrato no encontrado."
          : error.error?.message || "Error al actualizar el contrato.";
        return throwError(() => new Error(message));
      })
    );
  }

  getTodosLosContratos(): Observable<Contrato[]> {
    return this.http.get<Contrato[]>(this.apiUrl, {
      withCredentials: true
    }).pipe(
      catchError((error) => {
        const message = error.error?.message || "Error al obtener todos los contratos.";
        return throwError(() => new Error(message));
      })
    );
  }

  inactivateContrato(id: number): Observable<any> {
    const body = { estado: 0 };
    return this.http.put(`${this.apiUrl}/inactivar/${id}`, body, {
      withCredentials: true
    }).pipe(
      catchError((error) => {
        const message = error.status === 404
          ? "Contrato no encontrado."
          : error.error?.message || "Error al inactivar el contrato.";
        return throwError(() => new Error(message));
      })
    );
  }

  updateContratoPorId(id: number, contrato: Contrato): Observable<Contrato> {
    return this.http.put<Contrato>(`${this.apiUrl}/editar/id/${id}`, contrato, {
      withCredentials: true
    }).pipe(
      catchError((error) => {
        const message = error.status === 404
          ? "Contrato no encontrado."
          : error.error?.message || "Error al actualizar el contrato por ID.";
        return throwError(() => new Error(message));
      })
    );
  }

  getContratoPorCedula(cedula: number): Observable<Contrato[]> {
    return this.http.get<Contrato[]>(`${this.apiUrl}/contrato/${cedula}`, {
      withCredentials: true
    }).pipe(
      catchError((error) => {
        if (error.status === 404) {
          return of([]);
        }
        return throwError(() => error);
      })
    );
  }

  getContratoBypdf(numero: number): Observable<Contrato> {
    return this.http.get<Contrato>(`${this.apiUrl}/pdf/${numero}`, {
      withCredentials: true
    });
  }

  getContratoBypdfplantilla(numero: number): Observable<Contrato> {
    return this.http.get<Contrato>(`${this.apiUrl}/pdfplantilla/${numero}`, {
      withCredentials: true
    });
  }

  getContratoPorid(id: number): Observable<Contrato> {
    return this.http.get<Contrato>(`${this.apiUrl}/id/${id}`, {
      withCredentials: true
    });
  }

  buscarContratosPorFiltros(
    objeto?: string | number | Array<string | number>,
    contratista?: string,
    finicial?: Date | string,
    ffinal?: Date | string
  ): Observable<Contrato[]> {
    const body: any = {};
    const objetoVal = Array.isArray(objeto)
      ? objeto.filter((item) => item !== null && item !== undefined && item !== "")
      : objeto;

    if (objetoVal && (Array.isArray(objetoVal) ? objetoVal.length > 0 : true)) {
      body.objeto = objetoVal;
      body.tipo = objetoVal;
      if (Array.isArray(objetoVal)) {
        body.objetos = objetoVal;
        body.tipos = objetoVal;
      }
    }
    if (contratista) {
      body.contratista = contratista;
      body.contratante = contratista;
      body.cedula = contratista;
    }
    if (finicial) {
      body.finicial = finicial;
      body.inicio = finicial;
      body.fechaInicio = finicial;
    }
    if (ffinal) {
      body.ffinal = ffinal;
      body.fin = ffinal;
      body.fechaFin = ffinal;
    }

    return this.http.post<Contrato[]>(
      `${this.apiUrl}/buscar`,
      body,
      { withCredentials: true }
    );
  }
}
